import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  MdEngineering,
  MdStorefront,
  MdAttachMoney,
  MdChatBubbleOutline,
  MdChangeCircle,
  MdEventBusy,
  MdRefresh,
} from "react-icons/md";

import api from "utils/apiService";
import useAdminStats from "hooks/useAdminStats";
import KpiCard from "components/kpi/KpiCard";
import KpiStrip from "components/kpi/KpiStrip";
import QueueSection from "components/queue/QueueSection";
import QueueRow from "components/queue/QueueRow";
import RowActions from "components/queue/RowActions";
import PageHeader from "components/layout/PageHeader";
import {
  handleRequestAction,
  handleSupplierRequestAction,
  handleAcceptRejectPayment,
  handleCommercialRegisterRequest,
} from "actions/actions";

function relTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `منذ ${mins} د`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `منذ ${hours} س`;
  const days = Math.floor(hours / 24);
  return `منذ ${days} ي`;
}

function useQueueList(endpoint) {
  const [state, setState] = useState({ items: [], loading: true });
  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const res = await api.get(endpoint);
    if (res?.error) {
      setState({ items: [], loading: false });
    } else {
      setState({ items: res?.data || [], loading: false });
    }
  }, [endpoint]);
  useEffect(() => {
    refetch();
  }, [refetch]);
  return { ...state, refetch };
}

const Operations = () => {
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);

  const summary = useAdminStats("/admin/queues/summary");

  const contractorReq = useQueueList(
    "/users/requestingContractors?page=1&limit=5"
  );
  const supplierReq = useQueueList("/users/requestingSuppliers?page=1&limit=5");
  const payments = useQueueList(
    "/paymentRequests?page=1&limit=5&status=pending"
  );
  const crChanges = useQueueList(
    "/commercialRegisterChangeRequests?page=1&limit=5&status=pending"
  );
  const support = useQueueList("/supportMessages?page=1&limit=5");

  const summaryData = summary.data?.data || {};

  const refreshAll = () => {
    summary.refetch();
    contractorReq.refetch();
    supplierReq.refetch();
    payments.refetch();
    crChanges.refetch();
    support.refetch();
  };

  const runAction = async (id, fn) => {
    setBusyId(id);
    const res = await fn();
    setBusyId(null);
    if (res?.error) {
      toast.error(res.error);
    } else if (res?.success) {
      toast.success(res.success);
      refreshAll();
    }
  };

  return (
    <div className="mt-6">
      <PageHeader
        title="العمليات"
        subtitle="الطوابير التي تحتاج إلى إجراء"
        action={
          <button
            type="button"
            onClick={refreshAll}
            className="inline-flex items-center gap-1 rounded-lg border border-border-muted bg-white px-3 py-2 text-sm font-medium text-navy-700 hover:bg-info-50 dark:border-navy-700 dark:bg-navy-800 dark:text-white"
          >
            <MdRefresh className="h-4 w-4" />
            تحديث
          </button>
        }
      />

      <div className="mb-6">
        <KpiStrip columns={6}>
          <KpiCard
            icon={<MdEngineering className="h-5 w-5" />}
            title="مقاولون بانتظار القبول"
            value={summaryData.pendingContractors}
            loading={summary.loading}
            link="/admin/register-requests"
          />
          <KpiCard
            icon={<MdStorefront className="h-5 w-5" />}
            title="موردون بانتظار القبول"
            value={summaryData.pendingSuppliers}
            loading={summary.loading}
            link="/admin/supplier-register-requests"
          />
          <KpiCard
            icon={<MdAttachMoney className="h-5 w-5" />}
            title="طلبات دفع معلقة"
            value={summaryData.pendingPaymentRequests}
            loading={summary.loading}
            link="/admin/payments"
          />
          <KpiCard
            icon={<MdChatBubbleOutline className="h-5 w-5" />}
            title="شكاوى مفتوحة"
            value={summaryData.openSupportMessages}
            loading={summary.loading}
            link="/admin/complaints"
          />
          <KpiCard
            icon={<MdChangeCircle className="h-5 w-5" />}
            title="تغييرات سجل تجاري"
            value={summaryData.pendingCommercialRegisterChanges}
            loading={summary.loading}
            link="/admin/commercial-register-change-requests"
          />
          <KpiCard
            icon={<MdEventBusy className="h-5 w-5" />}
            title="سجلات تنتهي قريباً"
            value={summaryData.commercialRegisterExpiringSoon}
            loading={summary.loading}
            subtitle="خلال 30 يوم"
          />
        </KpiStrip>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <QueueSection
          title="طلبات تسجيل المقاولين"
          count={summaryData.pendingContractors}
          loading={contractorReq.loading}
          items={contractorReq.items}
          viewAllLink="/admin/register-requests"
          renderItem={(u) => (
            <QueueRow
              key={u._id || u.id}
              title={u.name || "—"}
              subtitle={u.phoneNumber}
              meta={
                <>
                  <span>{u.contractorInfo?.institutionName || ""}</span>
                  <span>·</span>
                  <span>{relTime(u.createdAt)}</span>
                </>
              }
              actions={
                <RowActions
                  busy={busyId === u._id}
                  onApprove={() =>
                    runAction(u._id, () =>
                      handleRequestAction(u._id, "accepted")
                    )
                  }
                  onReject={() =>
                    runAction(u._id, () =>
                      handleRequestAction(u._id, "rejected")
                    )
                  }
                  onView={() =>
                    navigate(`/admin/moqawelon?search=${u.phoneNumber || ""}`)
                  }
                />
              }
            />
          )}
        />

        <QueueSection
          title="طلبات تسجيل الموردين"
          count={summaryData.pendingSuppliers}
          loading={supplierReq.loading}
          items={supplierReq.items}
          viewAllLink="/admin/supplier-register-requests"
          renderItem={(u) => (
            <QueueRow
              key={u._id || u.id}
              title={u.name || "—"}
              subtitle={u.phoneNumber}
              meta={
                <>
                  <span>{u.supplierInfo?.institutionName || ""}</span>
                  <span>·</span>
                  <span>{relTime(u.createdAt)}</span>
                </>
              }
              actions={
                <RowActions
                  busy={busyId === u._id}
                  onApprove={() =>
                    runAction(u._id, () =>
                      handleSupplierRequestAction(u._id, "accepted")
                    )
                  }
                  onReject={() =>
                    runAction(u._id, () =>
                      handleSupplierRequestAction(u._id, "rejected")
                    )
                  }
                  onView={() =>
                    navigate(`/admin/suppliers?search=${u.phoneNumber || ""}`)
                  }
                />
              }
            />
          )}
        />

        <QueueSection
          title="طلبات الدفع المعلقة"
          count={summaryData.pendingPaymentRequests}
          loading={payments.loading}
          items={payments.items}
          viewAllLink="/admin/payments"
          renderItem={(p) => (
            <QueueRow
              key={p._id || p.id}
              title={`${p.amount || 0} ر.س`}
              subtitle={p.user?.name || p.user?.phoneNumber || "—"}
              meta={
                <>
                  <span>{p.type === "bank" ? "تحويل بنكي" : "بطاقة"}</span>
                  <span>·</span>
                  <span>{relTime(p.createdAt)}</span>
                </>
              }
              actions={
                <RowActions
                  busy={busyId === p._id}
                  onApprove={() =>
                    runAction(p._id, () =>
                      handleAcceptRejectPayment(p._id, "accept")
                    )
                  }
                  onReject={() =>
                    runAction(p._id, () =>
                      handleAcceptRejectPayment(p._id, "reject")
                    )
                  }
                  onView={() => navigate("/admin/payments")}
                />
              }
            />
          )}
        />

        <QueueSection
          title="طلبات تغيير السجل التجاري"
          count={summaryData.pendingCommercialRegisterChanges}
          loading={crChanges.loading}
          items={crChanges.items}
          viewAllLink="/admin/commercial-register-change-requests"
          renderItem={(r) => {
            const requestId = r.id || r._id;
            return (
              <QueueRow
                key={requestId}
                title={r.commercialRegister || "—"}
                subtitle={r.user?.name || r.user?.phoneNumber || "—"}
                meta={
                  <>
                    <span>
                      {r.commercialRegisterExpirationDate
                        ? `ينتهي ${r.commercialRegisterExpirationDate.slice(
                            0,
                            10
                          )}`
                        : ""}
                    </span>
                    <span>·</span>
                    <span>{relTime(r.createdAt)}</span>
                  </>
                }
                actions={
                  <RowActions
                    busy={busyId === requestId}
                    onApprove={() =>
                      runAction(requestId, () =>
                        handleCommercialRegisterRequest(requestId, "accept")
                      )
                    }
                    onReject={() =>
                      runAction(requestId, () =>
                        handleCommercialRegisterRequest(requestId, "reject")
                      )
                    }
                    onView={() =>
                      navigate("/admin/commercial-register-change-requests")
                    }
                  />
                }
              />
            );
          }}
        />

        <QueueSection
          title="الشكاوي المفتوحة"
          count={summaryData.openSupportMessages}
          loading={support.loading}
          items={support.items}
          viewAllLink="/admin/complaints"
          renderItem={(m) => (
            <QueueRow
              key={m._id || m.id}
              title={m.user?.name || m.user?.phoneNumber || "—"}
              subtitle={
                m.message?.length > 90
                  ? `${m.message.slice(0, 90)}…`
                  : m.message
              }
              meta={<span>{relTime(m.createdAt)}</span>}
              actions={
                <RowActions
                  busy={busyId === m._id}
                  onApprove={() =>
                    runAction(m._id, async () => {
                      const res = await api.patch(
                        `/supportMessages/${m._id}/resolve`,
                        { resolved: true }
                      );
                      if (res?.error) return { error: res.error };
                      return { success: "تم تحديد الشكوى كمحلولة" };
                    })
                  }
                  onView={() => navigate("/admin/complaints")}
                />
              }
            />
          )}
        />
      </div>
    </div>
  );
};

export default Operations;
